import { IconCheckmarkCircle } from '@tock/icons';
import { Link, Typography } from 'omakase';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import clsx from 'clsx';
import type { OmakaseTypographyVariant } from 'theme/typography/omakaseTypography';

type ResyCreditBannerProps = {
  includeDivider?: boolean;
  isResyCreditEligible?: boolean;
  titleMarginBottom?: number;
  titleVariant?: OmakaseTypographyVariant;
};

const ResyCreditBanner: React.FunctionComponent<ResyCreditBannerProps> = ({
  includeDivider,
  titleMarginBottom = 1,
  titleVariant = 'heading04',
  isResyCreditEligible = true,
}) => {
  const showResyCreditBanner = useFeatureFlagForConsumer('SHOW_RESY_CREDIT_BANNER_WEB');

  // During the period of 8/15/2026-9/14/2026 we will show an alternate language for the resy credit badging.
  // After this date we will revert to the previous language.
  const showAlternateResyCreditBadgingLanguage = useFeatureFlagForConsumer('RESY_CREDIT_TEMP_COPY');
  const resyCreditEligibleLanguage = showAlternateResyCreditBadgingLanguage
    ? 'Purchases made directly at this venue currently qualify for the Resy Credit, a benefit for select American Express® Card Members.'
    : 'This venue currently qualifies for the Resy Credit, a benefit for select American Express® Card Members.';
  const resyCreditIneligibleLanguage =
    "This venue doesn't currently qualify for the Resy Credit, a benefit for select American Express® Card Members.";
  if (!showResyCreditBanner) {
    return null;
  }
  return (
    <section
      className={clsx('ResyCreditBanner', {
        'ResyCreditBanner--divider': includeDivider,
      })}
      data-testid="resy-credit-banner"
    >
      <Typography component="h2" variant={titleVariant} mb={titleMarginBottom}>
        {isResyCreditEligible ? (
          <span className="ResyCreditBanner__title">
            Qualifies for Resy Credit
            <IconCheckmarkCircle />
          </span>
        ) : (
          'Resy Credit'
        )}
      </Typography>
      <Typography variant="body02" component="p">
        {isResyCreditEligible ? resyCreditEligibleLanguage : resyCreditIneligibleLanguage}{' '}
        <Link color="inherit" href="https://resy.com/resycredit">
          Terms apply.
        </Link>
      </Typography>
    </section>
  );
};

export default ResyCreditBanner;
